import React from 'react';
import { IconButtonProps, Menu, MenuItem, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotsHorizontal, DuplicateIcon, ShareIcon08 } from 'components/SVG';
import { useDialog, usePopover } from 'hooks/common';
import { useDeleteBoard } from 'hooks/Board';
import { useAddDuplicateBoard } from 'hooks/Board/useAddDuplicateBoard';
import DeleteMemoryQA from 'components/SVG/DeleteMemoryQA';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { IconButtonWrapper } from 'sharedComponents/UI';
import SparkResourceSharePopover from 'components/common/Share/SparkResourceSharePopover';
import { useResourceTypes } from 'hooks/EntityTypes/useResourceTypes';
import { SPARK_RESOURCE_TYPES } from 'constants/common';

type Props = {
  boardID: string;
  shouldRenderShareButton?: boolean;
  containerStyles?: IconButtonProps['sx'];
};

function BoardOptionsMenu({
  boardID,
  shouldRenderShareButton = false,
  containerStyles = {},
}: Props) {
  const { mutateAsync: deleteBoard } = useDeleteBoard(boardID);
  const { mutateAsync: addDuplicateBoard } = useAddDuplicateBoard(boardID);
  const { anchorEl, openPopover, closePopover } = usePopover();
  const { open, openDialog, closeDialog } = useDialog();
  const navigate = useNavigate();
  const {
    anchorEl: anchorElShare,
    open: openShare,
    openPopover: openSharePopover,
    closePopover: closeSharePopover,
  } = usePopover();
  const { idByKeyMap } = useResourceTypes();

  const handleDelete = async () => {
    closePopover();
    await deleteBoard({
      isDeleted: true,
    });
  };

  const handleDuplicate = async () => {
    const response = await addDuplicateBoard();
    closePopover();
    navigate(`/boards/${response?.data?.id}`);
  };

  const handleShare = (event: React.MouseEvent<HTMLLIElement>) => {
    closePopover();
    openSharePopover(event);
  };
  return (
    <>
      <IconButtonWrapper
        onClick={openPopover}
      >
        <DotsHorizontal strokeColor='#000' />
      </IconButtonWrapper>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ top: '5px' }}
      >
        {shouldRenderShareButton && (
          <MenuItem onClick={(event) => handleShare(event)}>
            <ShareIcon08 />
            <Typography component='span' variant='body1' sx={{ marginLeft: 1 }}>
              Share
            </Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleDuplicate}>
          <DuplicateIcon color='black' />
          <Typography sx={{ marginLeft: 1 }} component='span' variant='body1'>
            Duplicate
          </Typography>
        </MenuItem>
        <MenuItem onClick={openDialog}>
          <DeleteMemoryQA color='black' width='19' height='19' />
          <Typography sx={{ marginLeft: 1 }} component='span' variant='body1'>
            Delete
          </Typography>
        </MenuItem>
      </Menu>
      <ConfirmationDialog
        open={open}
        onClose={closeDialog}
        onPrimaryButtonClick={handleDelete}
        title='Delete Board?'
        confirmationMessage='Are you sure you want to delete this Board?'
      />
      {shouldRenderShareButton && openShare && (
        <SparkResourceSharePopover
          open={openShare}
          onClose={closeSharePopover}
          anchorEl={anchorElShare}
          resourceID={boardID}
          resourceTypeID={idByKeyMap?.[SPARK_RESOURCE_TYPES.BOARDS] || ''}
          placement='auto'
        />
      )}
    </>
  );
}

export default BoardOptionsMenu;
